// Helpers del admin viewer. Todo es puro y sin side effects.

import { corregirDominioTipeado } from './emailDominios';

const MES_ABBR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

// Lunes 00:00 de la semana ISO de la fecha dada.
const startOfIsoWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay() || 7; // 1..7 (lunes..domingo)
  d.setDate(d.getDate() - (day - 1));
  return d;
};

// Domingo 23:59:59.999 de la misma semana ISO.
const endOfIsoWeek = (date) => {
  const start = startOfIsoWeek(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

// Número de semana ISO 8601.
const getIsoWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

// Agrupa leads por semana. Devuelve [{ key, weekStart, weekEnd, label, leads }, ...] ordenado desc.
// Los leads sin fechaIso van al grupo "sin-fecha" al final.
export const groupByWeek = (leads) => {
  const buckets = new Map(); // key -> { weekStart, weekEnd, leads }
  for (const lead of leads) {
    if (!lead.fechaIso) {
      const k = 'sin-fecha';
      if (!buckets.has(k)) buckets.set(k, { weekStart: null, weekEnd: null, leads: [] });
      buckets.get(k).leads.push(lead);
      continue;
    }
    const date = new Date(lead.fechaIso);
    const ws = startOfIsoWeek(date);
    const we = endOfIsoWeek(date);
    const k = ws.toISOString().slice(0, 10);
    if (!buckets.has(k)) buckets.set(k, { weekStart: ws, weekEnd: we, leads: [] });
    buckets.get(k).leads.push(lead);
  }
  const out = [];
  for (const [key, value] of buckets.entries()) {
    out.push({
      key,
      weekStart: value.weekStart,
      weekEnd: value.weekEnd,
      label: formatWeekLabel(value.weekStart, value.weekEnd),
      leads: value.leads,
    });
  }
  out.sort((a, b) => {
    if (!a.weekStart) return 1;
    if (!b.weekStart) return -1;
    return b.weekStart.getTime() - a.weekStart.getTime();
  });
  return out;
};

// "W17 · 20 → 26 ABR 2026"  /  "W17 · 28 ABR → 4 MAY 2026"  /  "W01 · 30 DIC 2025 → 5 ENE 2026"
export const formatWeekLabel = (start, end) => {
  if (!start || !end) return 'SIN FECHA';
  const w = String(getIsoWeek(start)).padStart(2, '0');
  const sd = start.getDate();
  const sm = MES_ABBR[start.getMonth()];
  const sy = start.getFullYear();
  const ed = end.getDate();
  const em = MES_ABBR[end.getMonth()];
  const ey = end.getFullYear();
  if (sy !== ey) return `W${w} · ${sd} ${sm} ${sy} → ${ed} ${em} ${ey}`;
  if (sm !== em) return `W${w} · ${sd} ${sm} → ${ed} ${em} ${sy}`;
  return `W${w} · ${sd} → ${ed} ${em} ${sy}`;
};

export const isCurrentWeek = (weekStart) => {
  if (!weekStart) return false;
  const now = startOfIsoWeek(new Date());
  return now.getTime() === weekStart.getTime();
};

// "DD-MM HH:MM" derivado del string crudo de la columna A del Sheet
// ("26-04-2026, 7:17:31 p. m."). Parsea sin convertir TZ — el string ya
// está en hora Santiago, así que evitamos cualquier shift por TZ del
// server o del browser.
export const formatShortWhen = (rawFecha) => {
  if (!rawFecha || typeof rawFecha !== 'string') return '--';
  const m = rawFecha.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})[,\s]+(\d{1,2}):(\d{2})(?::\d{2})?\s*(a\.?\s*m\.?|p\.?\s*m\.?)?/i,
  );
  if (!m) return '--';
  const [, d, mo, , h, mi, ampm] = m;
  let hour = Number(h);
  if (ampm) {
    const isPm = /p/i.test(ampm);
    if (isPm && hour < 12) hour += 12;
    if (!isPm && hour === 12) hour = 0;
  }
  return `${String(d).padStart(2, '0')}-${String(mo).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${mi}`;
};

// El backend ya guarda la versión legible en "experiencia". La acortamos para el resumen compacto.
export const formatExperienciaShort = (texto) => {
  if (!texto) return '';
  const t = texto.toLowerCase();
  if (t.includes('menos de 6 meses')) return '<6m';
  if (t.includes('6 meses y 2 años') || t.includes('6 meses y 2 anos')) return '6m-2a';
  if (t.includes('más de 2 años') || t.includes('mas de 2 anos')) return '2a+';
  return texto;
};

export const formatSituacionShort = (texto) => {
  if (!texto) return '';
  const t = texto.toLowerCase();
  if (t.includes('no refleja')) return 'no refleja esfuerzo';
  if (t.includes('estancado')) return 'estancado';
  if (t.includes('competitivo') || t.includes('avanzado')) return 'nivel competitivo';
  if (t.includes('rutina nueva')) return 'rutina nueva';
  return texto;
};

export const formatUrgenciaShort = (texto) => {
  if (!texto) return '';
  const t = texto.toLowerCase();
  if (t.includes('ahora')) return 'ahora';
  if (t.includes('pronto')) return 'pronto';
  return texto;
};

// 'head' | 'team' | null según el texto de la columna `inversion` de Hoja 1.
// Es el ÚNICO lugar que lo decide: el filtro de coach y la card del lead lo
// usan los dos, para que nunca muestren planes distintos.
//
// El texto largo cambió con el tiempo y los leads viejos conservan el suyo:
//  - Head Coach antiguo: "Trabajar directamente con Agustín Urzúa (Head Coach)"
//  - Head Coach desde el 22-07-2026: "Quiere a Agustín · ..." (ya no dice
//    "head coach"; antes de este caso el filtro Head solo veía a los viejos)
//  - Team Coach: "Quiere equipo AU · ..." o "Trabajar con un coach del equipo
//    AU" (nunca dice "team coach"), por eso se matchea por "equipo"
//  - Ex Head: "Quería a Agustín · acepta equipo AU": con quién quería es
//    Agustín, pero en qué plan quedó es Team. Sigue siendo team.
//
// "Quiere a Agustín" va ANTES de "equipo" porque hay textos como "Quiere a
// Agustín · prefiere esperar, rechaza equipo AU" que contienen las dos cosas
// y el lead es Head.
const coachDeInversion = (inversion) => {
  const t = String(inversion || '').toLowerCase();
  if (t.includes('head coach')) return 'head';
  if (t.includes('quiere a agustín')) return 'head';
  if (t.includes('equipo')) return 'team';
  return null;
};

// "Head Coach $300.000" / "Team Coach $150.000" / "Ninguna opción"
// Los montos mostrados son los vigentes (leads antiguos pagaron precios
// anteriores, el label solo indica el plan).
export const formatPlanShort = (inversion) => {
  if (!inversion) return '';
  const coach = coachDeInversion(inversion);
  if (coach === 'head') return 'Head Coach $300.000';
  if (coach === 'team') return 'Team Coach $150.000';
  if (inversion.toLowerCase().includes('ninguna')) return 'Ninguna opción';
  return inversion;
};

// "+56912345678" -> "https://wa.me/56912345678" (sin '+', sin espacios, sin guiones)
export const formatWhatsappLink = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/[^\d]/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits}`;
};

// "@handle" o "handle" -> "https://instagram.com/handle"
export const formatInstagramLink = (raw) => {
  if (!raw) return null;
  const handle = String(raw).trim().replace(/^@+/, '').replace(/\s+/g, '');
  if (!handle) return null;
  return `https://instagram.com/${handle}`;
};

export const formatMailLink = (raw) => {
  if (!raw) return null;
  const email = String(raw).trim();
  if (!email.includes('@')) return null;
  return `mailto:${email}`;
};

// 'head' | 'team' | null — deriva el coach asignado a partir del campo `inversion` del lead.
// Las reglas y el porqué viven en coachDeInversion.
export const getCoachKey = (lead) => coachDeInversion(lead?.inversion);

// True cuando el lead originalmente pidió Head Coach con Agustín y aceptó
// pasar al equipo AU. Backend guarda el texto en pretérito imperfecto
// ("Quería a Agustín · acepta equipo AU...") para esta variante, mientras
// que los team coach directos van en presente ("Quiere equipo AU...").
export const isExHeadTeamCoach = (lead) => {
  const t = String(lead?.inversion || '').toLowerCase();
  return t.includes('quería a agustín') && t.includes('equipo');
};

// 'HC' | 'TC' | null — sigla para el badge del card.
export const getCoachBadge = (lead) => {
  const k = getCoachKey(lead);
  if (k === 'head') return 'HC';
  if (k === 'team') return 'TC';
  return null;
};

// Con qué coach del equipo va el lead. El valor viene de la columna AB de
// Hoja 1 ("Coach elegido"), que el formulario escribe con el nombre completo
// y sin tilde. Se mapea explícito y no se deriva del string suelto: un coach
// nuevo que nadie agregue acá tiene que mostrar "TC" pelado, no un nombre
// equivocado al lado del lead.
// 'agustin' no viene nunca de la columna AB (el formulario la deja vacía
// cuando el lead se queda con Agustín), pero sí es una clave válida del coach
// de cierre, que usa este mismo vocabulario. Está acá y no en un mapa aparte
// para que el nombre corto de cada coach viva en UN solo lugar.
const NOMBRE_CORTO_COACH = {
  'carlos dufeu': 'CARLOS',
  'alvaro niklitschek': 'ÁLVARO',
  agustin: 'AGUSTÍN',
};

/**
 * Nombre corto del team coach del lead, en mayúsculas, o null.
 *
 * Null también cuando el lead es Head Coach (no hay team coach que mostrar) y
 * cuando el dato no existe: los leads anteriores al 22-07-2026 son mayoría y
 * no lo tienen, porque el formulario todavía no preguntaba con qué coach del
 * equipo querían ir.
 */
export const getTeamCoachNombre = (lead) => {
  if (getCoachKey(lead) !== 'team') return null;
  const clave = String(lead?.coachElegido || '').trim().toLowerCase();
  return NOMBRE_CORTO_COACH[clave] || null;
};

/**
 * Texto completo del badge: 'HC', 'TC', 'TC · CARLOS', 'TC←HC · ÁLVARO'.
 * El nombre se agrega solo cuando se sabe.
 */
export const getCoachBadgeLabel = (lead) => {
  const sigla = getCoachBadge(lead);
  if (!sigla) return null;

  const base = isExHeadTeamCoach(lead) ? 'TC←HC' : sigla;
  const coach = getTeamCoachNombre(lead);
  return coach ? `${base} · ${coach}` : base;
};

// ── Con qué coach cerró de verdad ────────────────────────────────────────
// El badge de arriba sale del FORMULARIO: dice con quién quería ir el lead, no
// con quién terminó contratando. Francisco Puebla pidió Head Coach con Agustín
// y cerró con Álvaro, y la card no tenía cómo mostrarlo. Las claves son las
// mismas de la columna Coach de "Agenda Llamadas" (agendaBotConfig.COACHES).

export const COACHES_CIERRE = [
  { clave: 'agustin', label: 'Agustín' },
  { clave: 'Alvaro Niklitschek', label: 'Álvaro' },
  { clave: 'Carlos Dufeu', label: 'Carlos' },
];

/**
 * Nombre corto en mayúsculas del coach con quien está agendada la llamada, o
 * null si no se sabe. El dato es la columna Coach de "Agenda Llamadas", que el
 * bot escribe con la clave del coach (ver agendaBotConfig.COACHES).
 *
 * Es el ÚNICO dato de coach que tiene un lead de reserva directa: como nunca
 * pasó por el formulario, no hay `inversion` ni `coachElegido` de donde salga
 * el badge normal, y la card quedaba sin decir de quién era la llamada.
 */
export const getCoachAgendaNombre = (bot) => {
  const clave = String(bot?.coach || '').trim().toLowerCase();
  return NOMBRE_CORTO_COACH[clave] || null;
};

/** Nombre corto en mayúsculas del coach que cerró, o null si nadie lo marcó. */
export const getCoachCierreNombre = (bot) => {
  const clave = String(bot?.cerroCon || '').trim().toLowerCase();
  return NOMBRE_CORTO_COACH[clave] || null;
};

/**
 * Con qué coach quedaba este lead SEGÚN EL FORMULARIO, en clave de cierre.
 *
 * Null cuando no se puede saber: el lead que eligió equipo antes del 22-07-2026
 * no tiene coach elegido (el formulario todavía no lo preguntaba), y con eso no
 * hay contra qué comparar.
 */
const coachEsperado = (lead) => {
  const k = getCoachKey(lead);
  if (k === 'head') return 'agustin';
  if (k !== 'team') return null;
  const elegido = String(lead?.coachElegido || '').trim().toLowerCase();
  return NOMBRE_CORTO_COACH[elegido] ? elegido : null;
};

/**
 * ¿Cerró con un coach distinto del que pidió al postular?
 *
 * False también cuando falta cualquiera de los dos datos: solo se afirma que
 * hubo cambio cuando se conocen los dos lados y de verdad difieren.
 */
export const esCierreDeOtroCoach = (lead, bot) => {
  const cerro = String(bot?.cerroCon || '').trim().toLowerCase();
  if (!cerro || !NOMBRE_CORTO_COACH[cerro]) return false;
  const esperado = coachEsperado(lead);
  return Boolean(esperado) && esperado !== cerro;
};

// ── Resultado comercial de la llamada ────────────────────────────────────
// Las claves son exactamente las que guarda el backend en la columna P de
// "Agenda Llamadas" (ver backend/src/services/resultadoLlamadaCalc.js). Si
// cambian allá, hay que cambiarlas acá.

export const RESULTADO_LABEL = {
  contrato: 'CONTRATÓ',
  seguimiento: 'SEGUIMIENTO',
  no_show: 'NO SHOW',
  no_cerro: 'NO CERRÓ',
  definitivo_no: 'DIJO QUE NO',
  descartado: 'DESCARTADO',
};

// Orden en que se muestran los botones: del mejor desenlace al peor.
export const RESULTADOS_ORDEN = [
  'contrato', 'seguimiento', 'no_show', 'no_cerro', 'definitivo_no', 'descartado',
];

/**
 * Etiqueta del badge de resultado, o null cuando no hay ninguno.
 *
 * Null también para un valor que no se reconoce: la columna la puede editar
 * una persona a mano, y mostrar basura al lado del nombre del lead es peor que
 * no mostrar nada.
 */
export const getResultadoLabel = (bot) => RESULTADO_LABEL[String(bot?.resultado || '')] || null;

/** Clase CSS del badge. Cadena vacía cuando no hay resultado reconocible. */
export const getResultadoClass = (bot) => (
  RESULTADO_LABEL[String(bot?.resultado || '')] ? String(bot.resultado) : ''
);

/**
 * ¿Este lead sigue vivo en el funnel?
 *
 * No show, no cerró y seguimiento NO lo matan: siguen siendo perseguibles.
 * Solo salen los que compraron, los que dijeron que no definitivo y los que
 * Agustín descartó. Un lead sin fila del bot también está vivo: todavía no
 * agendó, pero nadie lo descartó.
 */
export const esLeadVivo = (lead) => {
  if (!lead?.cualificado) return false;
  const r = String(lead?.bot?.resultado || '');
  return r !== 'contrato' && r !== 'descartado' && r !== 'definitivo_no';
};

// "DD-MM" — fecha corta del momento en que se marcó como contactado.
// Acepta el timestamp crudo que guarda el backend ("26-04-2026 14:32:11").
export const formatContactadoDate = (raw) => {
  if (!raw) return '';
  const m = String(raw).match(/^(\d{1,2})[-/](\d{1,2})/);
  if (!m) return '';
  return `${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
};

/**
 * Fecha de la llamada en hora de Chile: "lun 04-08, 16:00".
 * El backend manda ISO, así que hay que forzar la zona: sin timeZone el
 * navegador la muestra en la zona del computador de quien mira.
 */
export const formatFechaLlamada = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const partes = new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    weekday: 'short', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const p = (t) => partes.find((x) => x.type === t)?.value || '';
  return `${p('weekday')} ${p('day')}-${p('month')}, ${p('hour')}:${p('minute')}`;
};

/**
 * Acorta la fecha de un envío/clic del bot a "DD-MM HH:MM", siempre en hora
 * de Chile. Este campo conviven DOS formatos según quién los escribió:
 * `ultimoEnvio` y `clicAgenda` vienen en ISO (`new Date().toISOString()`,
 * ver `agendaWatcher.js` y `agendaRedirectController.js`), mientras que
 * `bot.mensajes[].fecha` viene ya en "DD-MM-YYYY HH:MM:SS" hora Chile
 * (lo escribe `formatearFechaRegistro` en `mensajesSheetService.js`). Por
 * eso hay que detectar cuál de los dos es: el ISO SÍ se pasa por `Date`
 * forzando `timeZone: 'America/Santiago'` (si no, se ve en UTC y en Chile
 * la hora queda corrida); el string "DD-MM-YYYY..." NO se pasa por `Date`,
 * porque no trae zona y ya está en hora de Chile — convertirlo le agregaría
 * un corrimiento que el string no tiene.
 */
export const formatFechaCortaBot = (texto) => {
  const s = String(texto || '').trim();
  if (!s) return '';

  // ISO: "2026-08-01T15:17:49.209Z"
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const partes = new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    // Ojo: sin la opción `weekday`, el Intl de Node/V8 para 'es-CL' no
    // respeta el "2-digit" pedido y devuelve día/mes sin el cero adelante
    // (ej. "1" en vez de "01") — verificado a mano. Por eso se rellena acá
    // con padStart en vez de confiar en que Intl ya lo entregó paddeado.
    const p = (t) => (partes.find((x) => x.type === t)?.value || '').padStart(2, '0');
    return `${p('day')}-${p('month')} ${p('hour')}:${p('minute')}`;
  }

  // "DD-MM-YYYY HH:MM:SS", ya en hora Chile.
  const m = s.match(/^(\d{2})-(\d{2})-\d{4}[\s,]+(\d{2}):(\d{2})/);
  return m ? `${m[1]}-${m[2]} ${m[3]}:${m[4]}` : '';
};

/**
 * ¿La reunión de este lead ya ocurrió?
 *
 * Dos evidencias, y la primera manda: un RESULTADO marcado prueba que la
 * reunión existió, aunque el calendario no la tenga. Ese caso es real y no es
 * raro: si el lead reagenda por fuera del funnel, o la llamada se rehace
 * después de un no show, la fila queda sin fecha en la columna H y el
 * resultado se marca a mano. Con la fecha como única señal, esa persona
 * quedaba invisible en "Post llamada" justo cuando más había que perseguirla
 * (caso real: Solvey Muñoz, 15-08-2026).
 *
 * Si no hay resultado, se cae a la fecha de la cita, comparada contra el "hoy"
 * de Chile que calcula el llamador y no contra new Date(): así el panel y el
 * correo del bot usan el mismo criterio.
 */
export const tuvoLlamada = (lead, hoy) => {
  if (String(lead?.bot?.resultado || '').trim()) return true;
  const iso = String(lead?.bot?.fechaLlamada || '').trim();
  if (!iso) return false;
  return iso.slice(0, 10) <= hoy;
};

/** ¿Este lead cerró? Es el único resultado que escribe el sistema solo, cruzando con Flow. */
export const cerroContrato = (lead) => String(lead?.bot?.resultado || '').trim() === 'contrato';

/** ¿Este lead tiene un seguimiento que vence hoy o que ya venció? */
export const seguimientoPendiente = (lead, hoy) => {
  const bot = lead?.bot;
  if (!bot || String(bot.resultado || '') !== 'seguimiento') return false;
  const fecha = String(bot.contactarEl || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(fecha) && fecha <= hoy;
};

/**
 * Etiqueta de un resultado dentro del historial.
 *
 * El vacío es un valor real —así se limpia una marca— y por eso tiene su
 * propio texto en vez de quedar como un guion suelto. Un valor que no se
 * reconoce se muestra tal cual: esa columna la puede haber escrito una versión
 * vieja del sistema o una mano, y esconderlo dejaría un cambio sin explicar.
 */
export const labelResultadoHistorial = (valor) => {
  const v = String(valor || '').trim();
  if (!v) return 'SIN MARCA';
  return RESULTADO_LABEL[v] || v.toUpperCase();
};

/** "NO CERRÓ >> CONTRATÓ": de qué a qué pasó el lead en ese cambio. */
export const formatCambioResultado = (entrada) =>
  `${labelResultadoHistorial(entrada?.antes)} >> ${labelResultadoHistorial(entrada?.ahora)}`;

/**
 * Texto del seguimiento para la card. Las fechas son 'YYYY-MM-DD' y se restan
 * por componentes: pasar por new Date() correría el día en Chile.
 */
export const formatContactarEl = (contactarEl, hoy) => {
  const fecha = String(contactarEl || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return '';
  const hoyTexto = String(hoy || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(hoyTexto)) return '';

  const aUtc = (texto) => {
    const [a, m, d] = texto.split('-').map(Number);
    return Date.UTC(a, m - 1, d);
  };
  const dias = Math.round((aUtc(fecha) - aUtc(hoyTexto)) / 86400000);
  const [, mes, dia] = fecha.split('-');

  if (dias === 0) return 'contactar hoy';
  if (dias < 0) {
    const atraso = Math.abs(dias);
    return `VENCIDO hace ${atraso} ${atraso === 1 ? 'día' : 'días'}`;
  }
  return `contactar el ${dia}-${mes} (${dias === 1 ? 'falta 1 día' : `faltan ${dias} días`})`;
};

// ── Los envíos repetidos de una misma persona ────────────────────────────
//
// "Hoja 1" guarda un envío por cada vez que alguien completa el formulario, así
// que quien lo llena dos veces tenía dos cards y contaba dos veces. Medido el
// 15-08-2026: 152 filas calificadas eran 131 personas. Lo pilló Agustín viendo
// que José Hernández aparecía como dos contratos.
//
// Se agrupa acá, AL MOSTRAR, y nunca fusionando filas en la planilla: ese
// registro crudo es el que usan el bot y las campañas, y fusionarlo sería
// destructivo.

/**
 * Clave con la que dos envíos son la misma persona.
 *
 * Corrige el dominio mal tipeado antes de comparar, igual que ya hacen el cruce
 * de citas y el del cuestionario: sin eso, un `@gmail.con` y su `@gmail.com`
 * quedan como dos personas distintas y el problema reaparece por otra puerta.
 */
const claveDePersona = (email) => {
  const limpio = String(email || '').trim().toLowerCase();
  return corregirDominioTipeado(limpio) || limpio;
};

// Qué campos se miran para contar qué cambió entre un envío y el siguiente.
// El segundo valor es cómo se llama el campo en pantalla.
const CAMPOS_DEL_ENVIO = [
  ['nombre', 'nombre'],
  ['whatsapp', 'whatsapp'],
  ['instagram', 'instagram'],
  ['experiencia', 'experiencia'],
  ['situacion', 'situación'],
  ['urgencia', 'urgencia'],
  ['inversion', 'inversión'],
  ['segmento', 'segmento'],
  ['score', 'score'],
  ['coachElegido', 'coach elegido'],
];

/** Del más nuevo al más viejo. Los que no tienen fecha van al final. */
const ordenarPorFechaDesc = (envios) => [...envios].sort((a, b) => {
  if (!a.fechaIso && !b.fechaIso) return 0;
  if (!a.fechaIso) return 1;
  if (!b.fechaIso) return -1;
  return new Date(b.fechaIso) - new Date(a.fechaIso);
});

/**
 * Una entrada por persona a partir de sus envíos.
 *
 * Se muestran los datos del envío MÁS RECIENTE, que es el que corrigió errores
 * (José arregló su Instagram y su teléfono en el segundo), pero la fecha del
 * PRIMERO, que es cuando esa persona entró al funnel de verdad. Mezclar así es
 * deliberado: "cuándo llegó" y "qué dijo" responden preguntas distintas.
 *
 * Los envíos anteriores no se botan: quedan en `enviosAnteriores` con solo los
 * campos que cambiaron. En el caso de José, el primero es el único que guarda
 * que quería a Agustín y aceptó equipo, que es una distinción que el funnel
 * preserva a propósito.
 */
const construirPersona = (envios) => {
  const orden = ordenarPorFechaDesc(envios);
  const principal = orden[0];
  const primero = orden[orden.length - 1];

  return {
    ...principal,
    fecha: primero.fecha,
    fechaIso: primero.fechaIso,
    envios: orden.length,
    enviosAnteriores: orden.slice(1).map((viejo) => ({
      fecha: viejo.fecha,
      fechaIso: viejo.fechaIso,
      cambios: CAMPOS_DEL_ENVIO
        .filter(([campo]) => String(viejo[campo] ?? '') !== String(principal[campo] ?? ''))
        .filter(([campo]) => String(viejo[campo] ?? '').trim() !== '')
        .map(([campo, etiqueta]) => ({ campo: etiqueta, valor: viejo[campo] })),
    })),
  };
};

/**
 * Agrupa una lista de leads en una entrada por persona.
 * @param {Array} leads
 * @returns {Array} mismo shape del lead, más `envios` y `enviosAnteriores`.
 */
export const agruparPorPersona = (leads) => {
  const grupos = new Map();
  for (const lead of leads || []) {
    const clave = claveDePersona(lead?.email);
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(lead);
  }
  return [...grupos.values()].map(construirPersona);
};
